import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Drawer,
  P,
  Stack,
  Icon,
  Checkbox,
} from '@fashionnovateam/nova-ui-toolkit';
import { Prop65DataGrid } from './Prop65DataGrid';
import { PurchaseOrder } from '../../Lib/Types/purchase-order';

type Prop65DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  loading?: boolean;
  purchaseOrder: PurchaseOrder;
};

export function Prop65Drawer({
  isOpen,
  onClose,
  onSave,
  loading: l,
  purchaseOrder,
}: Prop65DrawerProps) {
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [prop65Values, setProp65Values] = useState<
    Record<string, 'yes' | 'no' | null>
  >({});
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(l || false);

  const hasAllValues = useMemo(
    () =>
      purchaseOrder.purchase_order_lines.every(
        (line) =>
          prop65Values[line.po_line_id] === 'yes' ||
          prop65Values[line.po_line_id] === 'no',
      ),
    [prop65Values, purchaseOrder.purchase_order_lines],
  );

  useEffect(() => {
    if (showErrors && hasAllValues) {
      setShowErrors(false);
    }
  }, [prop65Values, hasAllValues, showErrors]);
  const handleSubmitProp65 = (value: 'yes' | 'no') => {
    const hasAnySelection = Object.values(selectedRows).some(Boolean);

    const idsToUpdate = hasAnySelection
      ? Object.keys(selectedRows).filter((id) => selectedRows[id])
      : purchaseOrder.purchase_order_lines.map((line) =>
          line.po_line_id.toString(),
        );

    const updates = idsToUpdate.reduce(
      (acc, id) => ({
        ...acc,
        [id]: value,
      }),
      {},
    );

    setProp65Values((prev) => ({
      ...prev,
      ...updates,
    }));
    setSelectedRows({});
  };
  return (
    <Drawer width="666px" isOpen={isOpen} onClose={onClose}>
      <Drawer.Header className="px-10">
        <Drawer.Title className="mt-4">
          CA Proposition 65 Compliance Check
        </Drawer.Title>
        <Drawer.Description className="mt-2 text-sm text-base-disabled max-w-sm">
          Indicate whether each SKU requires a Prop 65 warning by selecting
          &apos;Yes&apos; or &apos;No&apos;.{' '}
          <span className="inline-flex">
            <a
              href="https://www.p65warnings.ca.gov/sites/default/files/art_6_business_qa.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-700 hover:text-primary-900"
              aria-label="View CA Prop 65 Guidelines"
              onClick={(e) => e.stopPropagation()}
            >
              View CA Prop 65 Guidelines
            </a>
          </span>
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Content className="px-10">
        <Stack direction="row" align="center">
          <P size="xsm" className="mr-2 min-w-[100px]" color="disabled">
            {Object.values(selectedRows).filter(Boolean).length > 0
              ? `${Object.values(selectedRows).filter(Boolean).length} Selected`
              : 'Set CA Prop 65'}
          </P>
          <Stack direction="row" gap="xs">
            <Button
              color="secondary"
              loading={loading}
              onClick={() => handleSubmitProp65('yes')}
              size="sm"
            >
              <Icon name="information/information-triangle" />
              {Object.values(selectedRows).some(Boolean)
                ? 'Set to Yes'
                : 'Yes to All'}
            </Button>
            <Button
              color="secondary"
              loading={loading}
              onClick={() => handleSubmitProp65('no')}
              size="sm"
            >
              <Icon name="check/check-tick-circle" />
              {Object.values(selectedRows).some(Boolean)
                ? 'Set to No'
                : 'No to All'}
            </Button>
          </Stack>
        </Stack>

        <div className="mt-6">
          <Prop65DataGrid
            loading={loading}
            purchaseOrder={purchaseOrder}
            selectedRows={selectedRows}
            prop65Values={prop65Values}
            onSelectionChange={setSelectedRows}
            onUpdateProp65={(id, value) => {
              setProp65Values((prev) => ({ ...prev, [id]: value }));
            }}
            showErrors={showErrors}
          />
        </div>

        <Stack direction="row" gap="sm" align="center" className="mt-8">
          <Checkbox
            checked={checkbox1}
            onChange={(e) => setCheckbox1(e.target.checked)}
            disabled={loading}
          />
          <P size="xsm">
            I understand once I save these selections, I will not be able to
            make further changes.
          </P>
        </Stack>

        <Stack direction="row" gap="sm" align="start" className="mt-4">
          <Checkbox
            checked={checkbox2}
            onChange={(e) => setCheckbox2(e.target.checked)}
            disabled={loading}
          />
          <P size="xsm">
            I acknowledge that all SKUs marked &ldquo;Yes&rdquo; will receive a
            CA Proposition 65 label, while those marked &ldquo;No&rdquo; do not
            require one.{' '}
            <strong>
              Since Proposition 65 labels are presented to end customers and
              might affect their purchasing decisions, it is crucial to
              accurately label items that are not subject to Prop 65
              requirements as &ldquo;No.&rdquo;
            </strong>
          </P>
        </Stack>

        <Button
          loading={loading}
          onClick={() => {
            if (!hasAllValues) {
              setShowErrors(true);
              return;
            }
            setLoading(true);
            onSave(prop65Values)
              .then(() => setLoading(false))
              .catch(() => setLoading(false));
          }}
          disabled={loading || !checkbox1 || !checkbox2}
          className="w-full mt-6"
        >
          Save Prop 65 Selections
        </Button>
      </Drawer.Content>
    </Drawer>
  );
}
